// Admin dashboard — pending queue with approve/reject actions.
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, X } from "lucide-react";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback((status) =>
    api.get(`/admin/tournaments?status=${status}`)
      .then(r => setRows(r.data || []))
      .catch(err => { console.warn("[admin] load failed", err); setRows([]); }),
  []);

  useEffect(() => {
    if (user?.role === "admin") load(tab);
  }, [tab, user, load]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-12 text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display font-black text-4xl uppercase tracking-tight">Admins only</h1>
        <p className="text-zinc-400 mt-3 text-sm">Sign in with the seeded admin phone <span className="font-mono text-court">9999999999</span>.</p>
      </div>
    );
  }

  const decide = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/tournaments/${id}/${action}`, { reason: "" });
      toast.success(`Tournament ${action}d`);
      load(tab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Admin</div>
      <h1 className="font-display font-black text-5xl uppercase tracking-tighter mt-2">Tournament Queue</h1>

      <div className="mt-8 flex gap-2 border-b border-zinc-800">
        {[
          ["pending", "Pending"],
          ["approved", "Approved"],
          ["rejected", "Rejected"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs font-bold tracking-widest uppercase border-b-2 -mb-px transition-colors ${
              tab === k ? "border-court text-court" : "border-transparent text-zinc-400 hover:text-white"
            }`}
          >{lbl}</button>
        ))}
      </div>

      <div className="mt-6 border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-charcoal">
            <tr className="text-left text-[10px] tracking-widest uppercase text-zinc-500">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Dates</th>
              <th className="px-4 py-3">Age</th>
              <th className="px-4 py-3">Fee</th>
              <th className="px-4 py-3">Organizer</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">No tournaments in this state.</td></tr>
            )}
            {rows.map(t => (
              <tr key={t.id} data-testid={`admin-row-${t.id}`} className="hover:bg-elevated/40">
                <td className="px-4 py-3 font-display font-bold text-base">{t.name}</td>
                <td className="px-4 py-3 text-zinc-400">{t.location}</td>
                <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{t.start_date} → {t.end_date}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{(t.age_categories || []).join(", ")}</td>
                <td className="px-4 py-3 text-zinc-400 font-mono">${t.entry_fee}</td>
                <td className="px-4 py-3 text-zinc-400">{t.organizer_name}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {tab !== "approved" && (
                      <button
                        data-testid={`admin-approve-${t.id}`}
                        disabled={busy === t.id + "approve"}
                        onClick={() => decide(t.id, "approve")}
                        className="px-3 h-9 bg-neon text-black font-bold text-xs tracking-widest uppercase active:scale-95 inline-flex items-center gap-1"
                      ><Check className="w-3.5 h-3.5" />Approve</button>
                    )}
                    {tab !== "rejected" && (
                      <button
                        data-testid={`admin-reject-${t.id}`}
                        disabled={busy === t.id + "reject"}
                        onClick={() => decide(t.id, "reject")}
                        className="px-3 h-9 bg-foul text-white font-bold text-xs tracking-widest uppercase active:scale-95 inline-flex items-center gap-1"
                      ><X className="w-3.5 h-3.5" />Reject</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
